/**
 * Werbung im Hauptbild: Sponsorenliste mit Werbematerial, Logo im Bild,
 * Banner, L-Form und Werbepause.
 *
 * Wie Einblendungen und Look liegt alles davon auf dem Server und nicht im
 * Regie-Tab. Nur so sehen Zuschauer, Regie-Monitor und der RTMP-Ausgang
 * dasselbe Logo zur selben Zeit -- und nur so laeuft eine Werbepause bei
 * allen gleichzeitig an und auch wieder aus, egal ob die Regie dazwischen
 * ihren Tab neu laedt.
 *
 * Bilder und Videos selbst gehen nie durch die WebSocket: sie liegen als
 * Bytes auf dem Server und werden ueber eigene URLs geholt, genau wie die
 * Bilder eines Events.
 */
import type { CornerSlot, WidgetSize } from './ticker.ts';

/* ---------------------------------------------------------- Werbematerial */

/**
 * Wofuer ein Stueck Werbematerial gedacht ist. Ein Sponsor bringt beliebig
 * viele Bilder und Videos mit; bei jedem hakt die Regie an, wo es laufen
 * soll. Ein Stueck darf an mehreren Stellen laufen -- ein Logo etwa in der
 * Ecke *und* im Laufband.
 */
export type MaterialUse = 'logo' | 'banner' | 'lshape' | 'break';
export type MaterialKind = 'image' | 'video';

export const MATERIAL_USES: { id: MaterialUse; label: string; kinds: MaterialKind[]; hint: string }[] = [
  {
    id: 'logo',
    label: 'Logo im Bild',
    kinds: ['image'],
    hint: 'Wechselt sich in der Ecke mit den anderen Logos ab und steht in der L-Form neben dem Spot.',
  },
  { id: 'banner', label: 'Banner', kinds: ['image'], hint: 'Rollt im Laufband mit bzw. steht als Bannerbild im Banner.' },
  { id: 'lshape', label: 'L-Form', kinds: ['image', 'video'], hint: 'Läuft in der L-Form — ein Video stumm in der Ecke.' },
  { id: 'break', label: 'Werbepause', kinds: ['video'], hint: 'Läuft als Werbespot groß und mit Ton.' },
];

/** Passt der Zweck zur Art? Ein Bild ist keine Werbepause, ein Video kein Logo. */
export const useFits = (use: MaterialUse, kind: MaterialKind): boolean =>
  MATERIAL_USES.some((u) => u.id === use && u.kinds.includes(kind));

/**
 * Ein Bild in der L-Form steht entweder in der Ecke, wo sonst der Spot
 * laeuft (`corner`), oder ist eine fertige L-Grafik ueber beide Streifen
 * (`full`). Die fertige Grafik ersetzt Logo und Werbetext -- die
 * Kennzeichnung „Werbung" bleibt.
 */
export type LShapeImageStyle = 'corner' | 'full';

export const LSHAPE_IMAGE_STYLES: { id: LShapeImageStyle; label: string; hint: string }[] = [
  { id: 'corner', label: 'Eckbild', hint: 'Steht in der Ecke, wo sonst der Spot läuft — Logo und Werbetext bleiben daneben.' },
  {
    id: 'full',
    label: 'L-Grafik',
    hint: 'Fertige Grafik in 1920 × 1080 über beide Streifen — was unter dem Live-Bild liegt, ist nicht zu sehen.',
  },
];

export interface SponsorMaterial {
  id: string;
  kind: MaterialKind;
  /** Fertige URL mit Zeitstempel. */
  url: string;
  /** Laenge eines Videos in ms; bei Bildern null. */
  durationMs: number | null;
  uses: MaterialUse[];
  lshapeStyle: LShapeImageStyle;
  /** Dateiname -- damit die Regie ihre Stuecke wiedererkennt. */
  label: string;
}

export interface Sponsor {
  id: string;
  name: string;
  /** Kurzer Werbetext fuers Laufband und die L-Form, z. B. „Energie für die letzte Runde". */
  tagline: string;
  /**
   * Farbe der L-Form, z. B. `#1f2a5c` -- oder null: dann nimmt jeder Player
   * sie aus dem Logo (`web/src/lib/sponsorColor.ts`).
   */
  color: string | null;
  /** Hinweis in der L-Form, z. B. „10 % mit RABBIT10" -- leer heisst: keiner. */
  cta: string;
  /** Adresse fuer den QR-Code, immer mit http(s) -- leer heisst: kein QR-Code. */
  link: string;
  materials: SponsorMaterial[];
}

export const MAX_CTA = 60;
export const MAX_LINK = 200;
export const SPONSOR_COLOR = /^#[0-9a-f]{6}$/i;

/**
 * Macht aus dem, was die Regie eintippt, eine Adresse fuer den QR-Code --
 * „rabbitfuel.de" wird zu „https://rabbitfuel.de". Alles, was keine
 * Web-Adresse ist, gibt null: ein QR-Code mit „javascript:" oder einer
 * Telefonnummer gehoert nicht in einen Stream.
 */
export function normalizeLink(input: string): string | null {
  const raw = input.trim();
  if (!raw || raw.length > MAX_LINK || /\s/.test(raw)) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (!url.hostname.includes('.')) return null;
    return withScheme;
  } catch {
    return null;
  }
}

/**
 * „rabbitfuel.de/shop" -- die Adresse so, wie man sie abtippen wuerde. Aus
 * dem Text und nicht ueber `URL`: der schriebe „münchen.de" als Punycode.
 */
export function linkLabel(link: string): string {
  return link
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');
}

/** Ein Stueck Material samt dem Sponsor, dem es gehoert. */
export interface PlacedMaterial {
  sponsor: Sponsor;
  material: SponsorMaterial;
}

/** Alles, was fuer diesen Zweck angehakt ist -- in der Reihenfolge der Liste. */
export const materialsFor = (ads: AdsState, use: MaterialUse): PlacedMaterial[] =>
  ads.sponsors.flatMap((sponsor) =>
    sponsor.materials
      .filter((material) => material.uses.includes(use) && useFits(use, material.kind))
      .map((material) => ({ sponsor, material })),
  );

export const findMaterial = (ads: AdsState, materialId: string): PlacedMaterial | null => {
  for (const sponsor of ads.sponsors) {
    const material = sponsor.materials.find((m) => m.id === materialId);
    if (material) return { sponsor, material };
  }
  return null;
};

/** Das Logo, mit dem ein Sponsor sich zeigt (L-Form, Regie) -- sein erstes Bild mit „Logo im Bild". */
export const sponsorLogo = (sponsor: Sponsor): string | null =>
  sponsor.materials.find((m) => m.kind === 'image' && m.uses.includes('logo'))?.url ?? null;

/** Werbespots fuer die Werbepause -- auch die, die der Zeitplan reihum zeigt. */
export const breakSpots = (ads: AdsState): PlacedMaterial[] => materialsFor(ads, 'break');

/** Mehr Stuecke je Sponsor braucht kein Event -- und jedes kostet Platz auf dem Server. */
export const MAX_MATERIALS = 12;

/**
 * Worauf das Logo steht. Ein Logo ist fuer hellen Grund gemacht oder fuer
 * dunklen -- ein weisses Logo auf weisser Platte waere unsichtbar, ein
 * dunkles direkt auf dem Kamerabild auch. Deshalb waehlt die Regie.
 */
export type SponsorPlate = 'light' | 'dark' | 'none';

export const SPONSOR_PLATES: { id: SponsorPlate; label: string; hint: string }[] = [
  { id: 'light', label: 'Hell', hint: 'Weiße Fläche hinter dem Logo — passt zu farbigen und dunklen Logos.' },
  { id: 'dark', label: 'Dunkel', hint: 'Dunkle Fläche — passt zu hellen und weißen Logos.' },
  { id: 'none', label: 'Ohne', hint: 'Logo direkt auf dem Bild — nur für freigestellte Logos.' },
];

/** Logo im Bild: wo, wie gross, wie lange je Sponsor. */
export interface SponsorLogoConfig {
  on: boolean;
  /**
   * Nur die vier Ecken. Ein Logo in einem der Baender ueber die volle Breite
   * waere ein Banner, kein Senderlogo -- und schoebe die Bauchbinde weg.
   */
  slot: CornerSlot;
  size: WidgetSize;
  plate: SponsorPlate;
  /** Sekunden je Logo, bevor das naechste kommt. */
  rotateSeconds: number;
}

/**
 * Wo das Live-Bild waehrend der Werbepause steht. Es laeuft klein weiter,
 * damit niemand etwas verpasst -- stumm, gehoert wird der Spot.
 */
export interface AdLiveLayout {
  slot: CornerSlot;
  size: WidgetSize;
}

/**
 * Eine laufende Werbepause.
 *
 * `elapsedMs` statt eines Startzeitpunkts: die Uhren von Server, Handy und
 * Laptop gehen gern ein paar Sekunden auseinander, und schon eine kleine
 * Abweichung liesse einen Spot bei manchen Zuschauern mittendrin anfangen.
 * Der Server rechnet die verstrichene Zeit beim Versand aus; der Empfaenger
 * zaehlt ab Eingang mit seiner eigenen Uhr weiter (`adBreakPosition`).
 */
export interface AdBreak {
  /** Neu fuer jede Pause -- derselbe Spot zweimal hintereinander ist zweimal ein Start. */
  id: string;
  sponsorId: string;
  /** Welches Video des Sponsors laeuft -- bei einer Botschaft leer. */
  materialId: string;
  sponsorName: string;
  spotUrl: string;
  durationMs: number;
  elapsedMs: number;
  /**
   * Statt eines Werbespots laeuft eine Botschaft (siehe clips.ts). Bild und
   * Ablauf sind dieselben -- gross im Bild, live klein in der Ecke --, nur
   * steht statt „Werbung" ein Namensschild da. `sponsorId` ist dann leer.
   */
  clip?: AdBreakClip;
}

export interface AdBreakClip {
  id: string;
  kind: 'video' | 'audio';
  /** Fertiger Text fuers Namensschild, z. B. „Anna Muster · Startnr. 12". */
  from: string;
}

/**
 * Werbung nach Zeitplan. `auto` startet die Pause selbst, warnt die Regie
 * aber vorher -- sie kann dann noch schieben. `confirm` meldet nur, dass eine
 * Pause faellig ist, und wartet, bis die Regie sie freigibt: so kann sie
 * vorher ansagen, dass es gleich weitergeht, oder eine spannende Szene
 * abwarten.
 */
export type AdScheduleMode = 'off' | 'auto' | 'confirm';

export const AD_SCHEDULE_MODES: { id: AdScheduleMode; label: string; hint: string }[] = [
  { id: 'off', label: 'Aus', hint: 'Werbepausen nur von Hand.' },
  {
    id: 'auto',
    label: 'Automatisch',
    hint: 'Die Pause startet von selbst. 30 Sekunden vorher kannst du sie noch schieben oder auslassen.',
  },
  {
    id: 'confirm',
    label: 'Mit Hinweis',
    hint: 'Ist eine Pause fällig, fragt die Regie nach und wartet, bis du sie freigibst.',
  },
];

export interface AdSchedule {
  mode: AdScheduleMode;
  /** Abstand zwischen zwei Pausen, in Minuten Sendezeit. */
  everyMinutes: number;
}

/**
 * Die naechste Pause nach Zeitplan.
 *
 * Gezaehlt wird Sendezeit, nicht Uhrzeit: solange kein Hauptbild auf Sendung
 * ist, steht der Zeitplan (`counting: false`). `inMs` gilt beim Versand, der
 * Empfaenger zaehlt mit der eigenen Uhr weiter -- dieselbe Idee wie bei
 * `AdBreak.elapsedMs`.
 */
export interface AdDue {
  sponsorId: string;
  sponsorName: string;
  /** Der Spot, der dran ist, und wie lang er ist. */
  materialId: string;
  durationMs: number;
  inMs: number;
  counting: boolean;
  /** Faellig und wartet auf die Regie -- nur im Modus „Mit Hinweis". */
  waiting: boolean;
}

/* ------------------------------------------------- Banner und L-Form */

/*
 * Werbung, ohne das Programm zu unterbrechen. Statt einer Pause rueckt das
 * Live-Bild zusammen und macht Platz -- fuer ein Banner oben oder unten oder
 * fuer ein L aus zwei Streifen, in dessen Ecke ein Spot stumm laeuft. Zu
 * hoeren bleibt das Live-Bild.
 *
 * Zusammengerueckt wird, nicht ueberdeckt: das Live-Bild wird als Ganzes
 * kleiner, samt kleinem Fenster und Einblendungen. So verdeckt die Werbung
 * nichts vom Rennen, und keine Einblendung muss ihr ausweichen.
 */

export type AdFrameKind = 'banner' | 'lshape';

export const AD_FRAME_KINDS: { id: AdFrameKind; label: string }[] = [
  { id: 'banner', label: 'Banner' },
  { id: 'lshape', label: 'L-Form' },
];

export type BannerEdge = 'top' | 'bottom';

export const BANNER_EDGES: { id: BannerEdge; label: string }[] = [
  { id: 'bottom', label: 'unten' },
  { id: 'top', label: 'oben' },
];

/**
 * Was im Banner laeuft. `crawl` braucht nichts Neues: Logos und Werbetexte
 * der Sponsorenliste rollen durch. `images` zeigt fertige Bannerbilder, die
 * der Sponsor liefert, eins nach dem anderen.
 */
export type BannerStyle = 'crawl' | 'images';

export const BANNER_STYLES: { id: BannerStyle; label: string; hint: string }[] = [
  { id: 'crawl', label: 'Laufband', hint: 'Logos und Werbetexte der Sponsoren rollen durch.' },
  { id: 'images', label: 'Bannerbilder', hint: 'Fertige Bannerbilder der Sponsoren, eins nach dem anderen.' },
];

/** Drei Staerken -- beim Banner seine Hoehe, bei der L-Form die Breite der Streifen. */
export type AdFrameSize = 'sm' | 'md' | 'lg';

export const AD_FRAME_SIZES: { id: AdFrameSize; label: string; short: string }[] = [
  { id: 'sm', label: 'Schmal', short: 'S' },
  { id: 'md', label: 'Mittel', short: 'M' },
  { id: 'lg', label: 'Breit', short: 'L' },
];

/**
 * Worauf Banner und L stehen. `sponsor` faerbt das L in der Farbe des
 * Sponsors, der gerade dran ist -- im Banner laufen mehrere Sponsoren
 * zugleich, dort gibt es nur Dunkel und Hell.
 */
export type AdFrameLook = 'sponsor' | 'dark' | 'light';

export const AD_FRAME_LOOKS: { id: AdFrameLook; label: string; hint: string }[] = [
  {
    id: 'sponsor',
    label: 'Sponsorfarbe',
    hint: 'Das L in der Farbe des Sponsors, der gerade dran ist — aus dem Logo oder beim Sponsor eingestellt.',
  },
  { id: 'dark', label: 'Dunkel', hint: 'Dunkle Fläche — passt zu hellen und weißen Logos.' },
  { id: 'light', label: 'Hell', hint: 'Helle Fläche — passt zu farbigen und dunklen Logos.' },
];

export interface AdBannerConfig {
  edge: BannerEdge;
  style: BannerStyle;
  size: AdFrameSize;
  /** Sekunden je Bannerbild, bevor das naechste kommt. */
  rotateSeconds: number;
  /** Nur Dunkel oder Hell -- siehe `AdFrameLook`. */
  look: Exclude<AdFrameLook, 'sponsor'>;
}

/**
 * Die L-Form. `corner` ist die Ecke, in der sich die beiden Streifen treffen
 * -- dort laeuft der Spot. Das Live-Bild rueckt in die gegenueberliegende
 * Ecke. Vier Ecken heisst: das L laesst sich in alle vier Richtungen drehen.
 */
export interface AdLShapeConfig {
  corner: CornerSlot;
  size: AdFrameSize;
  /** So lange steht ein Bild in der L-Form, bevor das naechste Stueck kommt. */
  imageSeconds: number;
  look: AdFrameLook;
}

export const LSHAPE_IMAGE_LIMITS = [4, 60] as const;

export const LSHAPE_CORNERS: { id: CornerSlot; label: string; icon: string }[] = [
  { id: 'bottomLeft', label: 'links + unten', icon: '┗' },
  { id: 'bottomRight', label: 'rechts + unten', icon: '┛' },
  { id: 'topLeft', label: 'links + oben', icon: '┏' },
  { id: 'topRight', label: 'rechts + oben', icon: '┓' },
];

/**
 * Ein laufendes Banner oder L. `elapsedMs` statt eines Startzeitpunkts --
 * derselbe Grund wie bei `AdBreak`.
 */
export interface AdFrame {
  /** Neu fuer jedes Einblenden. */
  id: string;
  kind: AdFrameKind;
  /** Wie lange es steht, oder null: bis die Regie es ausblendet. */
  durationMs: number | null;
  elapsedMs: number;
  /**
   * L-Form: das Material, das reihum laeuft (IDs) -- festgelegt beim
   * Einblenden, damit jeder Zuschauer dieselbe Reihenfolge rechnet, auch
   * wenn inzwischen etwas dazugekommen ist. Beim Banner leer.
   */
  items: string[];
  /**
   * Wo die L-Form in ihrer Reihe steht, in ms -- beim Versand. Meist gleich
   * `elapsedMs`; „Weiter" springt vor, ohne dass die Restzeit sich aendert.
   */
  positionMs: number;
  /** „Halten": das Stueck an `positionMs` bleibt stehen, ein Video laeuft in Schleife. */
  held: boolean;
}

/** Wo die L-Form gerade steht, von hier aus gesehen -- gehalten zaehlt nichts weiter. */
export const lshapePosition = (frame: AdFrame, receivedAt: number, now: number = performance.now()): number =>
  frame.held ? frame.positionMs : frame.positionMs + Math.max(0, now - receivedAt);

/** Wie lange ein Banner oder L stehen bleibt -- `null` heisst: bis zum Ausblenden. */
export const AD_FRAME_DURATIONS: { ms: number | null; label: string }[] = [
  { ms: null, label: 'bis Aus' },
  { ms: 30_000, label: '30 s' },
  { ms: 60_000, label: '1 min' },
  { ms: 120_000, label: '2 min' },
  { ms: 300_000, label: '5 min' },
];
/** Laenger steht nichts mit fester Dauer -- wer mehr will, nimmt „bis Aus". */
export const MAX_AD_FRAME_MS = 60 * 60_000;
export const MAX_TAGLINE = 100;

export const defaultAdBanner = (): AdBannerConfig => ({
  edge: 'bottom',
  style: 'crawl',
  size: 'md',
  rotateSeconds: 10,
  look: 'dark',
});
export const defaultAdLShape = (): AdLShapeConfig => ({
  corner: 'bottomLeft',
  size: 'md',
  imageSeconds: 10,
  look: 'sponsor',
});

/* ------------------------------------------------- L-Form nach Zeitplan */

/**
 * Die L-Form nach Zeitplan -- dieselben Modi wie bei den Werbepausen, aber
 * ein eigener Takt: das L unterbricht nichts und darf oefter kommen. Es
 * blendet sich selbst wieder aus, deshalb gibt es kein „bis Aus".
 */
export interface AdLShapeSchedule {
  mode: AdScheduleMode;
  everyMinutes: number;
  /** Wie lange sie dann steht, in ms -- oder `cycle`: bis alles einmal lief. */
  forMs: number | 'cycle';
}

export const LSHAPE_EVERY_PRESETS = [10, 15, 20, 30];
export const LSHAPE_FOR_OPTIONS: { value: number | 'cycle'; label: string }[] = [
  { value: 30_000, label: '30 s' },
  { value: 60_000, label: '1 min' },
  { value: 120_000, label: '2 min' },
  { value: 300_000, label: '5 min' },
  { value: 'cycle', label: 'alles einmal' },
];

export const defaultLShapeSchedule = (): AdLShapeSchedule => ({ mode: 'off', everyMinutes: 15, forMs: 60_000 });

/** Die naechste L-Form nach Zeitplan -- dieselbe Idee wie `AdDue`. */
export interface AdLShapeDue {
  inMs: number;
  counting: boolean;
  waiting: boolean;
  /** So lange wird sie stehen. */
  durationMs: number;
}

/** Bilder mit Haekchen „Banner" -- im Laufband rollen sie mit, als Bannerbilder wechseln sie sich ab. */
export const bannerImages = (ads: AdsState): PlacedMaterial[] => materialsFor(ads, 'banner');

/**
 * Was im Laufband steht: jeder Sponsor mit Bannerbild oder Werbetext, erst
 * seine Bilder, dann sein Text -- in der Reihenfolge der Liste.
 */
export const crawlEntries = (ads: AdsState): { sponsor: Sponsor; images: SponsorMaterial[] }[] =>
  ads.sponsors
    .map((sponsor) => ({
      sponsor,
      images: sponsor.materials.filter((m) => m.kind === 'image' && m.uses.includes('banner')),
    }))
    .filter((entry) => entry.images.length || entry.sponsor.tagline);

/**
 * Was das Banner tatsaechlich zeigt. Ist noch kein Bild als Banner
 * angehakt, laufen statt einer leeren Flaeche die Werbetexte durch.
 */
export const bannerStyleShown = (ads: AdsState): BannerStyle =>
  ads.banner.style === 'images' && bannerImages(ads).length ? 'images' : 'crawl';

/** Hat das Banner ueberhaupt etwas zu zeigen? */
export const bannerHasContent = (ads: AdsState): boolean => crawlEntries(ads).length > 0;

/** Ein Stueck der L-Form mit der Zeit, die es steht. */
export interface LShapeItem extends PlacedMaterial {
  ms: number;
}

/** Wie lange ein Stueck in der L-Form steht: ein Video so lang, wie es ist, ein Bild nach Einstellung. */
const lshapeMs = (material: SponsorMaterial, imageSeconds: number): number =>
  material.kind === 'video' ? (material.durationMs ?? 0) : imageSeconds * 1000;

/** Alles mit Haekchen „L-Form" -- fuer die Dauer „alles einmal" vor dem Einblenden. */
export const lshapeMaterials = (ads: AdsState): LShapeItem[] =>
  materialsFor(ads, 'lshape').map((placed) => ({ ...placed, ms: lshapeMs(placed.material, ads.lshape.imageSeconds) }));

/**
 * Das Material einer laufenden L-Form in der festgelegten Reihenfolge --
 * soweit es noch da ist und noch fuer die L-Form angehakt.
 */
export function lshapeItems(ads: AdsState, frame: AdFrame): LShapeItem[] {
  return frame.items
    .map((id) => findMaterial(ads, id))
    .filter((p): p is PlacedMaterial => Boolean(p && p.material.uses.includes('lshape') && useFits('lshape', p.material.kind)))
    .map((placed) => ({ ...placed, ms: lshapeMs(placed.material, ads.lshape.imageSeconds) }));
}

/** Ein Durchlauf, in ms. */
export const lshapeCycleMs = (items: { ms: number }[]): number => items.reduce((sum, item) => sum + item.ms, 0);

/** Wie lange eine L-Form nach Zeitplan steht -- „alles einmal" ist ein Durchlauf. */
export const lshapeScheduledMs = (ads: AdsState): number =>
  ads.lshapeSchedule.forMs === 'cycle' ? lshapeCycleMs(lshapeMaterials(ads)) : ads.lshapeSchedule.forMs;

/**
 * Welches Stueck der L-Form gerade laeuft und an welcher Stelle. Gerechnet
 * aus der verstrichenen Zeit, nicht gezaehlt: so laeuft bei allen Zuschauern
 * dasselbe an derselben Stelle, und wer spaeter dazukommt, springt hinein.
 * Nach dem letzten Stueck faengt es von vorn an.
 */
export function lshapeItemAt(
  items: LShapeItem[],
  positionMs: number,
): { item: LShapeItem; index: number; offsetMs: number; round: number } | null {
  const cycle = lshapeCycleMs(items);
  if (!items.length || cycle <= 0) return null;
  const round = Math.floor(Math.max(0, positionMs) / cycle);
  let left = Math.max(0, positionMs) - round * cycle;
  for (let index = 0; index < items.length; index++) {
    if (left < items[index].ms) return { item: items[index], index, offsetMs: left, round };
    left -= items[index].ms;
  }
  return { item: items[0], index: 0, offsetMs: 0, round: round + 1 };
}

/** Wo das naechste Stueck anfaengt -- dorthin springt „Weiter". */
export function lshapeNextStart(items: LShapeItem[], positionMs: number): number {
  const at = lshapeItemAt(items, positionMs);
  return at ? Math.max(0, positionMs) - at.offsetMs + at.item.ms : positionMs;
}

export interface AdsState {
  sponsors: Sponsor[];
  logo: SponsorLogoConfig;
  live: AdLiveLayout;
  break: AdBreak | null;
  schedule: AdSchedule;
  /** Null, solange der Zeitplan aus ist oder kein Sponsor einen Spot hat. */
  due: AdDue | null;
  banner: AdBannerConfig;
  lshape: AdLShapeConfig;
  /** Das laufende Banner oder L -- oder null. */
  frame: AdFrame | null;
  lshapeSchedule: AdLShapeSchedule;
  /** Null, solange der Zeitplan der L-Form aus ist oder nichts „L-Form" angehakt hat. */
  lshapeDue: AdLShapeDue | null;
}

export const AD_EVERY_LIMITS = [5, 240] as const;
export const AD_EVERY_PRESETS = [15, 30, 45, 60];
/** So lange vor einer automatischen Pause steht die Vorwarnung in der Regie. */
export const AD_WARN_MS = 30_000;
/** „Später" schiebt die Pause um so viel Sendezeit. */
export const AD_SNOOZE_MS = 5 * 60_000;

export const defaultAdSchedule = (): AdSchedule => ({ mode: 'off', everyMinutes: 45 });

export const ROTATE_LIMITS = [5, 120] as const;

export const defaultSponsorLogo = (): SponsorLogoConfig => ({
  on: false,
  slot: 'topRight',
  size: 'sm',
  plate: 'light',
  rotateSeconds: 15,
});

export const defaultAdLive = (): AdLiveLayout => ({ slot: 'bottomRight', size: 'md' });

export const emptyAds = (): AdsState => ({
  sponsors: [],
  logo: defaultSponsorLogo(),
  live: defaultAdLive(),
  break: null,
  schedule: defaultAdSchedule(),
  due: null,
  banner: defaultAdBanner(),
  lshape: defaultAdLShape(),
  frame: null,
  lshapeSchedule: defaultLShapeSchedule(),
  lshapeDue: null,
});

/** Laenger darf ein Spot nicht sein -- eine Werbepause ist kein Programm. */
export const MAX_SPOT_SECONDS = 180;
/** Groesste Datei, die die Regie hochladen darf (vor dem Umwandeln). */
export const MAX_SPOT_UPLOAD_BYTES = 200 * 1024 * 1024;
export const MAX_SPONSORS = 20;
export const MAX_SPONSOR_NAME = 60;

/** Die Logos, die gerade im Bild wechseln -- in der Reihenfolge der Liste. */
export const rotatingLogos = (ads: AdsState): PlacedMaterial[] => materialsFor(ads, 'logo');

/**
 * Welches Logo gerade dran ist. Gerechnet aus der Uhrzeit, nicht gezaehlt:
 * so wechseln alle Zuschauer zur selben Sekunde, ohne dass der Server im
 * Takt etwas schicken muss -- dieselbe Idee wie bei der Ticker-Uhr.
 */
export function currentLogoIndex(count: number, rotateSeconds: number, now: number = Date.now()): number {
  if (count <= 1) return 0;
  const period = Math.max(ROTATE_LIMITS[0], rotateSeconds) * 1000;
  return Math.floor(now / period) % count;
}

/** `0:12` -- Restzeit einer Werbepause. */
export function formatSpotTime(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
